import React, { useRef, useState } from "react";
import { Grid, IconButton, Typography } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";

export function RecipeImagesGallery({ images, onDelete, onReorder, disabled }) {
	const items = images || [];
	const dragIndexRef = useRef(null);
	const touchStartRef = useRef({ x: 0, y: 0, index: null });
	const lastUpdateTimeRef = useRef(0);
	const [draggingIndex, setDraggingIndex] = useState(null);
	const [dragOverIndex, setDragOverIndex] = useState(null);
	if (items.length === 0)
		return null;

	function handleDragStart(index) {
		if (disabled)
			return;
		dragIndexRef.current = index;
		setDraggingIndex(index);
	}

	function handleDrop(index, event) {
		if (disabled)
			return;
		event.preventDefault();
		const dragIndex = dragIndexRef.current;
		if (dragIndex === null || dragIndex === index)
			return;
		const next = [...items];
		const [moved] = next.splice(dragIndex, 1);
		next.splice(index, 0, moved);
		onReorder && onReorder(next);
		dragIndexRef.current = null;
		setDraggingIndex(null);
		setDragOverIndex(null);
	}

	function handleDragOver(event) {
		if (disabled)
			return;
		event.preventDefault();
	}

	function handleDragEnd() {
		setDraggingIndex(null);
		setDragOverIndex(null);
		dragIndexRef.current = null;
	}

	function handleDragEnter(index) {
		setDragOverIndex(index);
	}

	function handleTouchStart(index, event) {
		const touch = event.touches[0];
		touchStartRef.current = {
			x: touch.clientX,
			y: touch.clientY,
			index: index
		};
	}

	function handleTouchMove(event) {
		if (touchStartRef.current.index === null) return;
		
		const touch = event.touches[0];
		const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
		const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
		const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
		
		// Start dragging if moved more than 10px
		if (distance > 10) {
			dragIndexRef.current = touchStartRef.current.index;
			setDraggingIndex(touchStartRef.current.index);
		}
		
		// Prevent scrolling once drag is active
		if (dragIndexRef.current !== null) {
			event.preventDefault();
			
			// Throttle elementFromPoint to ~60fps (every 16ms)
			const now = Date.now();
			if (now - lastUpdateTimeRef.current >= 16) {
				lastUpdateTimeRef.current = now;
				
				// Find which item we're over
				const element = document.elementFromPoint(touch.clientX, touch.clientY);
				if (element) {
					const gridItem = element.closest('[data-image-index]');
					if (gridItem) {
						const overIndex = parseInt(gridItem.dataset.imageIndex, 10);
						setDragOverIndex(overIndex);
					}
				}
			}
		}
	}

	function handleTouchEnd(event) {
		if (dragIndexRef.current === null || dragOverIndex === null) {
			// Reset if no drag occurred or no target
			touchStartRef.current = { x: 0, y: 0, index: null };
			dragIndexRef.current = null;
			setDraggingIndex(null);
			setDragOverIndex(null);
			return;
		}

		const dragIndex = dragIndexRef.current;
		const dropIndex = dragOverIndex;
		
		if (dragIndex !== dropIndex) {
			const next = [...items];
			const [moved] = next.splice(dragIndex, 1);
			next.splice(dropIndex, 0, moved);
			onReorder && onReorder(next);
		}
		
		touchStartRef.current = { x: 0, y: 0, index: null };
		dragIndexRef.current = null;
		setDraggingIndex(null);
		setDragOverIndex(null);
	}

	return (
		<div className="rb-image-gallery">
			<Typography variant="h6" color="primary" style={{ marginBottom: 8 }}>
				Images
			</Typography>
			<Grid container spacing={16}>
				{items.map((item, index) => (
					<Grid
						item
						xs={6}
						md={3}
						key={item.id || item.url || item.previewUrl || index}
						data-image-index={index}
						style={{
							marginBottom: 12,
							paddingRight: 8,
							paddingLeft: 8,
							opacity: draggingIndex === index ? 0.4 : 1,
							transition: "opacity 0.2s"
						}}
						draggable={!disabled}
						onDragStart={() => handleDragStart(index)}
						onDragOver={handleDragOver}
						onDragEnter={() => handleDragEnter(index)}
						onDragEnd={handleDragEnd}
						onDrop={(e) => handleDrop(index, e)}
						onTouchStart={(e) => handleTouchStart(index, e)}
						onTouchMove={handleTouchMove}
						onTouchEnd={handleTouchEnd}>
						<div
							className="rb-image-thumb"
							style={{
								border: dragOverIndex === index && draggingIndex !== index ? "2px solid #2196f3" : "none",
								transition: "border 0.2s"
							}}>
							<div className="rb-image-drag-handle" aria-label="Drag to reorder">
								<DragIndicatorIcon fontSize="small" />
							</div>
							<img src={item.previewUrl ? item.previewUrl : `${item.url}/400x400`} alt="Recipe" loading="lazy" />
							{onDelete ? (
								<IconButton
									size="small"
									onClick={() => onDelete(item)}
									title="Delete image"
									disabled={disabled}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							) : null}
						</div>
					</Grid>
				))}
			</Grid>
		</div>
	);
}

// Basic styles scoped to gallery
const STYLE_ID = "rb-image-gallery-styles";
if (!document.getElementById(STYLE_ID)) {
	const styleEl = document.createElement("style");
	styleEl.id = STYLE_ID;
	styleEl.innerHTML = `
.rb-image-gallery img { 
	width: 100%; 
	height: auto; 
	max-height: 126px; 
	object-fit: contain; 
	border-radius: 6px; 
	border: 1px solid #e0e0e0; 
	background: #fafafa; 
}
.rb-image-thumb { 
	position: relative; 
	margin-bottom: 12px; 
	padding: 4px; 
}
.rb-image-thumb button { 
	position: absolute; 
	top: 6px; 
	right: 6px; 
	background: rgba(255,255,255,0.9); 
}
.rb-image-thumb button:hover { 
	background: rgba(255,200,200,0.95); 
}
.rb-image-drag-handle { 
	position: absolute; 
	top: 6px; 
	left: 6px; 
	opacity: 0.6; 
	cursor: grab; 
	background: rgba(255,255,255,0.8); 
	border-radius: 50%; 
	padding: 2px; 
	touch-action: none; 
	user-select: none; 
	-webkit-user-select: none; 
}
.rb-image-drag-handle:active { 
	cursor: grabbing; 
	opacity: 0.9; 
}
@media (max-width: 768px) {
	.rb-image-drag-handle { 
		opacity: 0.8; 
		padding: 4px; 
	}
}
`;
	document.head.appendChild(styleEl);
}
